import React from 'react';
import styled from 'styled-components';

export type VisibilityOption = 'internal' | 'customer-visible';

export interface VisibilitySelectorProps {
  /** Current selected visibility option */
  value: VisibilityOption;
  /** Called when selection changes */
  onChange: (value: VisibilityOption) => void;
  /** Optional label for the selector */
  label?: string;
  /** Show helper text */
  showHelper?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Helper = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const OptionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const OptionCard = styled.button<{ $active: boolean }>`
  border: 1.5px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.gray300)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.primary}10` : theme.colors.surface)};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  min-width: 200px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const OptionTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const OptionDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.3;
`;

export const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({
  value,
  onChange,
  label = 'Visibility',
  showHelper = true,
}) => {
  return (
    <Container>
      {label && <Label>{label}</Label>}
      {showHelper && <Helper>Who can see this?</Helper>}
      <OptionsContainer>
        <OptionCard
          $active={value === 'internal'}
          onClick={() => onChange('internal')}
          type="button"
        >
          <OptionTitle>Internal only</OptionTitle>
          <OptionDescription>Teams and operations only</OptionDescription>
        </OptionCard>
        <OptionCard
          $active={value === 'customer-visible'}
          onClick={() => onChange('customer-visible')}
          type="button"
        >
          <OptionTitle>Customer visible</OptionTitle>
          <OptionDescription>Visible to customer + internal teams</OptionDescription>
        </OptionCard>
      </OptionsContainer>
    </Container>
  );
};
